import { Chip, Icon, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useMemo, useState } from 'react'

import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { Caption } from 'src/feature/localization/component/atom/Caption'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { Palette } from 'src/feature/theme/model/Palette'
import { iconMapping } from 'src/util/icon-mapping'

import { RatingChart } from '../../model/my-team-skill-gaps'

type ChartHeaderProps = {
  title?: string
  icon?: string
  iconColor: string
  filterButtons?: React.ReactNode
}

export type HorizontalBarChartProps = {
  title?: string
  chartData?: RatingChart[]
  isLoading?: boolean
  icon?: string
  onItemClick?: (itemName: string) => void
  filterButtons?: React.ReactNode
  alternateData?: RatingChart[]
  alternateTitle?: string
  toggleLabels?: { primary: string; alternate: string }
}

function ChartHeader({ title, icon, iconColor, filterButtons }: ChartHeaderProps) {
  if (!title && !icon && !filterButtons) {
    return null
  }

  return (
    <AppBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <AppBox gap={5} alignItems="center">
        {icon && <Icon sx={{ ...styles.icon, color: iconColor }} component={iconMapping[icon]} />}
        <Typography sx={styles.title} noWrap>{title}</Typography>
      </AppBox>
      {filterButtons && <AppBox>{filterButtons}</AppBox>}
    </AppBox>
  )
}

function getBarColor(rating: number): string {
  if (rating >= 4) {
    return Palette.OLIVE_GREEN
  }
  if (rating >= 2) {
    return Palette.GOLDEN_ORANGE
  }

  return Palette.DANGER
}

function getIconColor(icon?: string): string {
  if (icon === 'ArrowUpwardRoundedIcon') {
    return 'secondary.main'
  }
  if (icon === 'ArrowDownwardRoundedIcon') {
    return Palette.DANGER
  }

  return 'secondary.main'
}

export const HorizontalBarChart = observer(({
  title,
  chartData,
  isLoading = false,
  icon,
  onItemClick,
  filterButtons,
  alternateData,
  alternateTitle,
  toggleLabels,
}: HorizontalBarChartProps) => {
  const { translation } = useLocalizationStore()
  const [isPrimary, setIsPrimary] = useState(true)

  // Determine which data to display based on toggle state
  const currentTitle = toggleLabels && !isPrimary ? alternateTitle : title
  const currentData = toggleLabels && !isPrimary ? alternateData : chartData

  // Generate toggle buttons if toggleLabels provided, otherwise use filterButtons
  const toggleButtons = useMemo(() => {
    if (!toggleLabels) {
      return filterButtons
    }

    return (
      <AppBox sx={{ display: 'flex', gap: { xs: 4, sm: 5 } }}>
        <Chip
          label={toggleLabels.primary}
          onClick={() => setIsPrimary(true)}
          variant={isPrimary ? 'filled' : 'outlined'}
          color={isPrimary ? 'primary' : 'default'}
          sx={styles.chip}
        />
        <Chip
          label={toggleLabels.alternate}
          onClick={() => setIsPrimary(false)}
          variant={!isPrimary ? 'filled' : 'outlined'}
          color={!isPrimary ? 'primary' : 'default'}
          sx={styles.chip}
        />
      </AppBox>
    )
  }, [toggleLabels, isPrimary, filterButtons])

  const iconColor = getIconColor(icon)
  const barData = currentData || []

  // Loading state
  if (isLoading) {
    return (
      <AppPaper sx={styles.root}>
        <ChartHeader title={currentTitle} icon={icon} iconColor={iconColor} filterButtons={toggleButtons} />
        <AppBox sx={styles.box}>
          <AppSkeleton
            count={1}
            direction="column"
            rects={[{ width: '100%', height: 120, color: 'background.dark' }]}
          />
        </AppBox>
      </AppPaper>
    )
  }

  // Empty state
  if (barData.length === 0) {
    return (
      <AppPaper sx={styles.root}>
        <ChartHeader title={currentTitle} icon={icon} iconColor={iconColor} filterButtons={toggleButtons} />
        <AppBox sx={styles.centeredBox}>
          <Caption sx={styles.noDataText}>{translation.noData()}</Caption>
        </AppBox>
      </AppPaper>
    )
  }

  // Main render
  return (
    <AppPaper sx={styles.root}>
      <ChartHeader title={currentTitle} icon={icon} iconColor={iconColor} filterButtons={toggleButtons} />
      <AppBox sx={styles.box}>
        <AppBox sx={styles.chartContainer}>
          {barData.map((item) => {
            const rating = item.avgSkillRating || 0
            const expectedRating = item.avgExpectedSkillRating || 0
            const ratingPercentage = (rating / 5) * 100
            const expectedPercentage = (expectedRating / 5) * 100
            const barColor = getBarColor(rating)
            
            return (
              <AppBox
                key={item.name}
                sx={{
                  ...styles.barRow,
                  cursor: onItemClick ? 'pointer' : 'default',
                  padding: '4px 6px',
                  borderRadius: '10px',
                  '&:hover': onItemClick ? { backgroundColor: 'action.hover' } : {},
                }}
              >
                <Tooltip
                  title={
                    expectedRating > 0 ? (
                      <>
                        {item.name}
                        <br />
                        (Expected rating: {expectedRating.toFixed(1)})
                      </>
                    ) : item.name
                  }
                  placement="top"
                  arrow
                >
                  <AppBox
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}
                    onClick={() => onItemClick?.(item.name)}
                  >
                    <Typography sx={styles.label} noWrap>
                      {item.name}
                    </Typography>
                    <AppBox sx={styles.barValueRow}>
                      <AppBox sx={styles.barContainer}>
                        <AppBox sx={styles.barBase} />
                        {expectedRating > 0 && (
                          <AppBox
                            sx={{
                              ...styles.barExpected,
                              width: `${expectedPercentage}%`,
                              backgroundColor: barColor,
                            }}
                          />
                        )}
                        <AppBox
                          sx={{
                            ...styles.barActual,
                            width: `${ratingPercentage}%`,
                            backgroundColor: barColor,
                          }}
                        />
                      </AppBox>
                      <Typography sx={styles.value}>
                        {expectedRating > 0 ? `${rating.toFixed(2)}/5.00` : rating.toFixed(2)}
                      </Typography>
                    </AppBox>
                  </AppBox>
                </Tooltip>
              </AppBox>
            )
          })}
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    width: '100%',
    minHeight: '240px',
    borderRadius: '10px',
    padding: '10px 15px',
    flexDirection: 'column',
    gap: 15,
    '@keyframes growWidth': {
      '0%': {
        width: '0%',
      },
    },
  },
  title: {
    fontSize: 11,
    color: 'text.secondary',
  },
  icon: {
    height: 15,
    width: 15,
  },
  box: {
    width: '100%',
    display: 'flex',
  },
  centeredBox: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
  },
  chartContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    width: '100%',
  },
  barRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    width: '100%',
  },
  barValueRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
  },
  label: {
    fontSize: 12,
    fontWeight: 400,
  },
  barContainer: {
    flex: 1,
    height: '13px',
    borderRadius: '10px',
    overflow: 'hidden',
    position: 'relative',
    display: 'flex',
    border: '0.5px solid',
    borderColor: 'text.secondary',
  },
  barBase: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    borderRadius: '10px',
    backgroundColor: 'background.dark',
    zIndex: 1,
  },
  barExpected: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: '10px',
    opacity: 0.2,
    zIndex: 2,
    animation: 'growWidth 0.8s ease-out forwards',
  },
  barActual: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    borderRadius: '10px',
    zIndex: 3,
    animation: 'growWidth 0.8s ease-out forwards',
    animationDelay: '0.2s',
  },
  value: {
    fontSize: 12,
    fontWeight: 400,
    color: 'text.primary',
    textAlign: 'right',
    minWidth: '60px',
  },
  noDataText: {
    color: 'text.secondary',
    fontSize: 11,
  },
  chip: {
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    padding: '2px 1px',
    height: 'auto',
    fontSize: 10,
    fontWeight: 500,
    '&:hover': {
      backgroundColor: 'action.hover',
    },
    '&.MuiChip-filled': {
      backgroundColor: 'primary.main',
      color: 'primary.contrastText',
    },
  },
}